/**
 * Crew messages in the job pack (accept or decline a shift, running late, job change notices) and set-up photos,
 * plus the office's view of them on a shift in Jobs.
 */
import { useRef, useState, type FormEvent } from "react";
import { Icon } from "../../components/Icon";
import { Notice, useNotice } from "../../components/Notice";
import { Chip } from "../../components/States";
import { BRAND } from "../../lib/config";
import { fmtDate, fmtDay, fmtTime } from "../../lib/format";
import { openBlobInNewTab } from "../../lib/jobs-data";
import { useAsync } from "../../lib/useAsync";
import { SectionHelp } from "../../help/links";
import {
  ackNotices, addShiftPhoto, repliesToChase, crewMessagesInUse, myNotices, officePhotoBlob, replyToShift, runningLate, shiftMessages, shiftPhotoBlob, shiftPhotos, smsHref,
  type LateSent, type ShiftReply,
} from "./messages-data";

export { crewMessagesInUse };

/* ------------------------------------------------------------------ notices (crew home) */

/** Changes to my shifts since I last looked, with "Got it". */
export function NoticesBanner({ onAcknowledged }: { onAcknowledged?: () => void }) {
  const s = useAsync(myNotices);
  const [busy, setBusy] = useState(false);
  const list = s.data?.notices ?? [];
  if (!crewMessagesInUse() || !list.length) return null;
  return (
    <div className="card" role="status">
      <div className="card__head"><h2 className="card__title">{list.length === 1 ? "1 change to your shifts" : `${list.length} changes to your shifts`}</h2><SectionHelp pageId="my-shifts">About changes</SectionHelp></div>
      <ul className="list">
        {list.map((n) => (
          <li key={n.id} className="list__row">
            <span>{n.bookingId ? <a href={`#/shift/${n.bookingId}`}>{n.message}</a> : n.message}<span className="sub">{fmtDate(n.createdAt)}</span></span>
          </li>
        ))}
      </ul>
      <div className="card__body">
        <button type="button" className="btn btn--ink btn--block" disabled={busy} aria-busy={busy} onClick={() => {
          setBusy(true);
          ackNotices().then(() => { s.reload(); onAcknowledged?.(); }).catch(() => undefined).finally(() => setBusy(false));
        }}>Got it</button>
      </div>
    </div>
  );
}

/** On a shift card: whether I have confirmed it. */
export function ReplyChip({ reply }: { reply: ShiftReply | null | undefined }) {
  if (reply === undefined) return null;
  if (reply?.response === "accepted") return <Chip tone="green">Confirmed</Chip>;
  if (reply?.response === "declined") return <Chip tone="grey">You can't do this one</Chip>;
  return <Chip tone="accent">Confirm this shift</Chip>;
}

/* ------------------------------------------------------------------ accept or decline (job pack) */

export function ShiftReplyCard({ bookingId, reply, endsAt, onChanged }: { bookingId: string; reply: ShiftReply | null; endsAt: string; onChanged: () => void }) {
  const [declining, setDeclining] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const { notice, setNotice, fail, ok } = useNotice();
  if (!crewMessagesInUse()) return null;
  const finished = Date.parse(endsAt) < Date.now();
  const send = (response: "accepted" | "declined") => {
    setBusy(true);
    setNotice(null);
    replyToShift(bookingId, response, response === "declined" ? reason : undefined)
      .then(() => {
        setDeclining(false);
        ok(response === "accepted" ? "Thanks, you're confirmed." : "Thanks. The office has been told.");
        onChanged();
      })
      .catch(fail)
      .finally(() => setBusy(false));
  };
  return (
    <div className="card" id="reply">
      <div className="card__head"><h2 className="card__title">Can you do this shift?</h2><SectionHelp pageId="my-shifts">How confirming works</SectionHelp></div>
      <div className="card__body stack">
        <Notice notice={notice} onDismiss={() => setNotice(null)} />
        {reply?.response === "accepted" && <p className="doc__done"><Icon name="check" size={18} />You confirmed this shift{reply.respondedAt ? `, ${fmtDate(reply.respondedAt)}` : ""}.</p>}
        {reply?.response === "declined" && <p className="small">You said you can't do it: {reply.declineReason}</p>}
        {!reply?.response && !finished && <p className="small">Let the office know you'll be there. If the time or site changes, you'll be asked again.</p>}
        {!finished && !declining && (
          <div className="packactions">
            {reply?.response !== "accepted" && <button type="button" className="btn btn--ink" disabled={busy} onClick={() => send("accepted")}><Icon name="check" size={18} /> I'll be there</button>}
            {reply?.response !== "declined" && <button type="button" className="btn btn--outline" disabled={busy} onClick={() => setDeclining(true)}>I can't do it</button>}
          </div>
        )}
        {declining && (
          <form className="stack" onSubmit={(e: FormEvent) => { e.preventDefault(); send("declined"); }}>
            <label className="field">
              <span className="field__label">Why can't you do it?</span>
              <textarea className="input" rows={2} maxLength={500} required value={reason} onChange={(e) => setReason(e.target.value)} />
            </label>
            <div className="packactions">
              <button type="submit" className="btn btn--primary" disabled={busy || reason.trim().length < 2} aria-busy={busy}>Tell the office</button>
              <button type="button" className="btn btn--outline" onClick={() => setDeclining(false)}>Back</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ running late (job pack) */

export function RunningLateCard({ bookingId, startsAt, endsAt }: { bookingId: string; startsAt: string; endsAt: string }) {
  const [minutes, setMinutes] = useState<number | null>(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState<LateSent | null>(null);
  const { notice, setNotice, fail } = useNotice();
  const cfg = BRAND.crewMessages;
  const now = Date.now();
  if (!cfg || Date.parse(startsAt) - now > 12 * 3600_000 || Date.parse(endsAt) < now) return null;
  return (
    <div className="card" id="late">
      <div className="card__head"><h2 className="card__title">Running late?</h2></div>
      <div className="card__body stack">
        <Notice notice={notice} onDismiss={() => setNotice(null)} />
        {sent ? (
          <>
            <p className="doc__done"><Icon name="check" size={18} />The office{sent.text.to.some((t) => t.label !== BRAND.name) ? " and your leading hand have" : " has"} been emailed.</p>
            {sent.text.to.map((t) => (
              <a key={t.phone} className="btn btn--ink btn--block" href={smsHref(t.phone, sent.text.body)}><Icon name="external" size={16} /> Text {t.label.split(" ")[0]}</a>
            ))}
            <button type="button" className="btn btn--outline btn--block" onClick={() => { setSent(null); setMinutes(null); setNote(""); }}>Send another</button>
          </>
        ) : (
          <form className="stack" onSubmit={(e) => {
            e.preventDefault();
            if (!minutes) return;
            setBusy(true);
            setNotice(null);
            runningLate(bookingId, minutes, note).then(setSent).catch(fail).finally(() => setBusy(false));
          }}>
            <div className="packactions" role="radiogroup" aria-label="How late">
              {cfg.lateMinutes.map((m) => (
                <button key={m} type="button" role="radio" aria-checked={minutes === m} className={`btn btn--sm ${minutes === m ? "btn--ink" : "btn--outline"}`} onClick={() => setMinutes(m)}>{m} min</button>
              ))}
            </div>
            <label className="field"><span className="field__label">Note (optional)</span><input className="input input--lg" maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Accident on the highway" /></label>
            <button type="submit" className="btn btn--primary btn--block" disabled={!minutes || busy} aria-busy={busy}>Tell them I'm running late</button>
          </form>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ set-up photos (job pack) */

/** The shift's set-up photos and adding one. `min` shows how many the start checklist needs. */
export function SetupPhotos({ bookingId, min, onChanged }: { bookingId: string; min?: number; onChanged?: (count: number) => void }) {
  const s = useAsync(() => shiftPhotos(bookingId), [bookingId]);
  const [caption, setCaption] = useState("");
  const [busy, setBusy] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const { notice, setNotice, fail } = useNotice();
  const photos = s.data?.photos ?? [];
  const max = s.data?.max ?? 10;
  const add = (file: File | undefined) => {
    if (!file) return;
    setBusy(true);
    setNotice(null);
    addShiftPhoto(bookingId, file, caption)
      .then(() => {
        setCaption("");
        s.reload();
        onChanged?.(photos.length + 1);
      })
      .catch(fail)
      .finally(() => {
        setBusy(false);
        if (input.current) input.current.value = "";
      });
  };
  return (
    <div className="stack" style={{ gap: 8 }}>
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      <p className="small" style={{ margin: 0 }}>
        {photos.length === 0 ? "No photos yet." : `${photos.length} of ${max} photos.`}
        {min && photos.length < min ? ` At least ${min} needed.` : ""}
      </p>
      {photos.length > 0 && (
        <ul className="list">
          {photos.map((p, i) => (
            <li key={p.id} className="list__row">
              <span>{p.caption ?? `Photo ${i + 1}`}<span className="sub">{p.crewName ?? ""} {fmtTime(p.createdAt)}</span></span>
              <button type="button" className="btn btn--outline btn--xs" onClick={() => void openBlobInNewTab(() => shiftPhotoBlob(bookingId, p.id))}>View</button>
            </li>
          ))}
        </ul>
      )}
      {s.data?.canAdd && photos.length < max && (
        <>
          <label className="field"><span className="field__label">What it shows (optional)</span><input className="input" maxLength={200} value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="e.g. Northern approach" /></label>
          <label className="field">
            <span className="field__label">{busy ? "Uploading the photo" : "Take or add a photo"}</span>
            <input ref={input} className="input" type="file" accept="image/jpeg,image/png,image/webp" capture="environment" disabled={busy} onChange={(e) => add(e.target.files?.[0])} style={{ paddingTop: 9 }} />
          </label>
        </>
      )}
    </div>
  );
}

export function SetupPhotosCard({ bookingId }: { bookingId: string }) {
  return (
    <div className="card" id="photos">
      <div className="card__head"><h2 className="card__title">Set-up photos</h2><SectionHelp pageId="my-shifts">About set-up photos</SectionHelp></div>
      <div className="card__body"><SetupPhotos bookingId={bookingId} /></div>
    </div>
  );
}

/* ------------------------------------------------------------------ office (Jobs) */

/** A person's reply on a shift in Jobs. */
export function ReplyMark({ response, reason }: { response?: string | null; reason?: string | null }) {
  if (!crewMessagesInUse()) return null;
  if (response === "accepted") return <span title="Confirmed" aria-label="Confirmed"><Icon name="check" size={14} /></span>;
  if (response === "declined") return <Chip tone="primary">Can't do it{reason ? `: ${reason}` : ""}</Chip>;
  return <span className="sub" title="Not confirmed yet">not confirmed</span>;
}

/** Running late messages and set-up photos on a shift in Jobs. */
export function ShiftMessagesCell({ bookingId }: { bookingId: string }) {
  const s = useAsync(() => shiftMessages([bookingId]), [bookingId]);
  if (!s.data || (!s.data.lates.length && !s.data.photos.length)) return null;
  return (
    <div className="stack" style={{ gap: 4 }}>
      {s.data.lates.map((l) => (
        <span key={l.id} className="small sub--primary">{l.crew_members?.full_name ?? "Crew"}: running {l.minutes} min late ({fmtTime(l.created_at)}){l.note ? `. ${l.note}` : ""}</span>
      ))}
      {s.data.photos.length > 0 && (
        <span className="small">
          Set-up photos:{" "}
          {s.data.photos.map((p, i) => (
            <span key={p.id}>
              {i > 0 && ", "}
              <button type="button" className="linkbtn" title={[p.caption, p.crew_members?.full_name].filter(Boolean).join(", ")} onClick={() => void openBlobInNewTab(() => officePhotoBlob(p.id))}>{p.caption ?? `photo ${i + 1}`}</button>
            </span>
          ))}
        </span>
      )}
    </div>
  );
}

/** Today: shifts in the next 3 days with a decline or someone not confirmed. */
export function ShiftRepliesCard() {
  const s = useAsync(() => repliesToChase());
  const rows = s.data;
  const declines = rows?.filter((r) => r.declined.length).length ?? 0;
  return (
    <div className={`card${declines ? " card--alert" : ""}`}>
      <div className="card__head"><h2 className="card__title">Crew replies</h2><SectionHelp pageId="jobs">How replies work</SectionHelp></div>
      {s.loading && !rows && <p className="small muted" style={{ margin: "0 16px 12px" }}>Loading</p>}
      {s.error && <p className="small sub--primary" style={{ margin: "0 16px 12px" }}>{s.error}</p>}
      {rows && rows.length === 0 && <p className="small muted" style={{ margin: "0 16px 12px" }}>Everyone on the next 3 days of shifts has confirmed.</p>}
      {rows && rows.length > 0 && (
        <ul className="list">
          {rows.map((r) => (
            <li key={r.bookingId} className="list__row">
              <span>
                {r.jobId ? <a className="strong" href={`#/jobs/${r.jobId}`}>{r.reference} {r.title}</a> : <strong>Shift with no job</strong>}
                <span className="sub">{fmtDay(r.startsAt)} {fmtTime(r.startsAt)}</span>
                {r.declined.map((d) => <span key={d.name} className="sub sub--primary">{d.name} can't do it{d.reason ? `: ${d.reason}` : ""}</span>)}
                {r.waiting.length > 0 && <span className="sub">Not confirmed: {r.waiting.join(", ")}</span>}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
